import { useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Heading,
  Link,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import { CollapsibleProcessorFileRow } from '#/components/collapsible-processor-file-row';
import { GET_AI_AUDITS } from '#/graphql/queries/ai-audit';
import { GET_PROCESSOR } from '#/graphql/queries/processor-detail';
import { GET_PROCESSOR_INSTANCES_BY_PROCESSOR } from '#/graphql/queries/processor-instances-by-processor';

export function ProcessorDetailPage() {
  const { processorId } = useParams<{ processorId: string }>();

  const { data, loading, error, refetch } = useQuery(GET_PROCESSOR, {
    variables: { id: processorId ?? '' },
    skip: !processorId,
  });

  const { data: instancesData, loading: instancesLoading } = useQuery(
    GET_PROCESSOR_INSTANCES_BY_PROCESSOR,
    {
      variables: { processorId: processorId ?? '', limit: 50 },
      skip: !processorId,
    },
  );

  const { data: auditsData, loading: auditsLoading } = useQuery(GET_AI_AUDITS, {
    variables: {
      limit: 50,
      endpoint: null,
      processorId: processorId ?? null,
    },
    skip: !processorId,
  });

  const detail = data?.processor;
  const instanceRows = instancesData?.processorInstances ?? [];
  const auditRows = auditsData?.aiAudits ?? [];

  if (!processorId) {
    return (
      <Box>
        <Text color="red.500">Missing processor id.</Text>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={4}>
          Processor
        </Heading>
        <Text color="fg.muted">Loading…</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={4}>
          Processor
        </Heading>
        <Text color="red.500">Error: {error.message}</Text>
        <Button mt={4} size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box>
        <Heading size="lg" color="fg" mb={4}>
          Processor not found
        </Heading>
        <Text color="fg.muted" mb={4}>
          No processor with id &quot;{processorId}&quot;.
        </Text>
      </Box>
    );
  }

  return (
    <Box p={4}>
      <VStack gap={6} align="stretch">
        <Box>
          <Heading size="lg" color="fg">
            {detail.id}
          </Heading>
        </Box>

        <VStack align="start" gap={1}>
          <Text fontSize="sm" color="fg.muted">
            Main file id
          </Text>
          <Text fontSize="sm" fontFamily="mono" wordBreak="break-all">
            {detail.fileId}
          </Text>
          <Text fontSize="xs" color="fg.muted" mt={2}>
            Updated {new Date(detail.updatedAt).toLocaleString()}
          </Text>
        </VStack>

        <Box>
          <Heading size="md" color="fg" mb={3}>
            Processor files
          </Heading>
          <Text fontSize="sm" color="fg.muted" mb={4}>
            Expand a row to load and view source. Order matches execution
            (prepends first, then main).
          </Text>
          <VStack gap={3} align="stretch">
            {detail.fileRefs.map((ref) => (
              <CollapsibleProcessorFileRow
                key={ref.id}
                fileId={ref.id}
                sortOrder={ref.sortOrder}
                role={ref.role}
              />
            ))}
          </VStack>
        </Box>

        <Box>
          <Heading size="md" color="fg" mb={3}>
            Processor instances
          </Heading>
          <Text fontSize="sm" color="fg.muted" mb={3}>
            Your recent runs of this processor (newest first).
          </Text>
          {instancesLoading ? (
            <Text color="fg.muted" fontSize="sm">
              Loading…
            </Text>
          ) : instanceRows.length === 0 ? (
            <Text color="fg.muted" fontSize="sm">
              No processor instances yet.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm" variant="line">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Created</Table.ColumnHeader>
                    <Table.ColumnHeader>Status</Table.ColumnHeader>
                    <Table.ColumnHeader>Error</Table.ColumnHeader>
                    <Table.ColumnHeader>Id</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {instanceRows.map((row) => (
                    <Table.Row key={row.id}>
                      <Table.Cell whiteSpace="nowrap">
                        {new Date(row.createdAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell>{row.status}</Table.Cell>
                      <Table.Cell
                        fontSize="xs"
                        maxW="200px"
                        css={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={row.error ?? undefined}
                      >
                        {row.error ?? '—'}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="xs">
                        {row.id}
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Link asChild color="blue.600">
                          <RouterLink to={`/instance/${row.id}`}>
                            View
                          </RouterLink>
                        </Link>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Box>

        <Box>
          <Heading size="md" color="fg" mb={3}>
            AI audit log
          </Heading>
          <Text fontSize="sm" color="fg.muted" mb={3}>
            OpenAI audit rows whose metadata matches this processor id.
          </Text>
          {auditsLoading ? (
            <Text color="fg.muted" fontSize="sm">
              Loading…
            </Text>
          ) : auditRows.length === 0 ? (
            <Text color="fg.muted" fontSize="sm">
              No matching audit rows.
            </Text>
          ) : (
            <Box overflowX="auto">
              <Table.Root size="sm" variant="line">
                <Table.Header>
                  <Table.Row>
                    <Table.ColumnHeader>Created</Table.ColumnHeader>
                    <Table.ColumnHeader>Endpoint</Table.ColumnHeader>
                    <Table.ColumnHeader>Instance id</Table.ColumnHeader>
                    <Table.ColumnHeader>Id</Table.ColumnHeader>
                    <Table.ColumnHeader textAlign="end"> </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {auditRows.map((row) => (
                    <Table.Row key={row.id}>
                      <Table.Cell whiteSpace="nowrap">
                        {new Date(row.createdAt).toLocaleString()}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="xs">
                        {row.endpoint}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="xs">
                        {row.instanceId ?? '—'}
                      </Table.Cell>
                      <Table.Cell fontFamily="mono" fontSize="sm">
                        {row.id}
                      </Table.Cell>
                      <Table.Cell textAlign="end">
                        <Link asChild color="blue.600">
                          <RouterLink to={`/ai-audit/${row.id}`}>
                            View
                          </RouterLink>
                        </Link>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Root>
            </Box>
          )}
        </Box>
      </VStack>
    </Box>
  );
}
